import React from 'react';
import PropTypes from 'prop-types';
import {
  TextField,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import DescribedField from '../DescribedField';

const emptyItem = () => ({
  id: crypto.randomUUID(),
  name: '',
  description: '',
  duration: '',
  price: '',
});

/**
 * ServicesBlockEditor — Editor do bloco Serviços
 * TODO 4.8
 *
 * Props:
 *   - value: { items: [{ id, name, description, duration, price }] }
 *   - onChange: (patch) => void
 *   - context: { siteType, availableServices, ... }
 */
const ServicesBlockEditor = ({ value = {}, onChange }) => {
  const { items = [] } = value;

  const patchItems = (newItems) => onChange({ ...value, items: newItems });

  const addItem = () => patchItems([...items, emptyItem()]);

  const removeItem = (index) => patchItems(items.filter((_, i) => i !== index));

  const updateItem = (index, field, val) => {
    const updated = items.map((item, i) =>
      i === index ? { ...item, [field]: val } : item
    );
    patchItems(updated);
  };

  return (
    <DescribedField
      label="Lista de serviços"
      description="Serviços oferecidos com nome, descrição opcional, duração e preço. Você pode preencher livremente ou importar dos serviços cadastrados. Quando exibido junto do bloco de Agendas, ajuda o visitante a conhecer as opções antes de agendar. Campos opcionais vazios não aparecem na página."
      helperLink={{ entity: 'service' }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((item, index) => (
          <Paper key={item.id ?? index} variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">Serviço {index + 1}</Typography>
              <IconButton size="small" onClick={() => removeItem(index)} aria-label="Remover serviço">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            <TextField
              fullWidth
              label="Nome"
              value={item.name}
              onChange={(e) => updateItem(index, 'name', e.target.value)}
              size="small"
              sx={{ mb: 1.5 }}
              helperText="Nome do serviço. Importe dos serviços cadastrados ou preencha livremente."
            />
            <TextField
              fullWidth
              label="Descrição (opcional)"
              value={item.description}
              onChange={(e) => updateItem(index, 'description', e.target.value)}
              size="small"
              multiline
              rows={2}
              sx={{ mb: 1.5 }}
              helperText="Explicação curta do que está incluso. Opcional."
            />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <TextField
                label="Duração"
                value={item.duration}
                onChange={(e) => updateItem(index, 'duration', e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                helperText="Ex.: '60min'"
              />
              <TextField
                label="Preço"
                value={item.price}
                onChange={(e) => updateItem(index, 'price', e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                helperText="Ex.: 'R$ 200,00'. Vazio não exibe."
              />
            </Box>
          </Paper>
        ))}

        <Button
          startIcon={<AddIcon />}
          onClick={addItem}
          variant="outlined"
          size="small"
          sx={{ alignSelf: 'flex-start' }}
        >
          Adicionar serviço
        </Button>
      </Box>
    </DescribedField>
  );
};

ServicesBlockEditor.propTypes = {
  value: PropTypes.shape({
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        description: PropTypes.string,
        duration: PropTypes.string,
        price: PropTypes.string,
      })
    ),
  }),
  onChange: PropTypes.func.isRequired,
  context: PropTypes.object,
};

export default ServicesBlockEditor;
